import AttachFileIcon from '@mui/icons-material/AttachFile';
import EventIcon from '@mui/icons-material/Event';
import HistoryIcon from '@mui/icons-material/History';
import LinkIcon from '@mui/icons-material/Link';
import NotesIcon from '@mui/icons-material/Notes';
import PercentIcon from '@mui/icons-material/Percent';
import PersonIcon from '@mui/icons-material/Person';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import ShieldIcon from '@mui/icons-material/Shield';
import ShortTextIcon from '@mui/icons-material/ShortText';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import type { IDependency, IIssue, IRisk, RiskImpact, RiskProbability } from '@pulse/shared';
import {
  Alert,
  Button,
  Chip,
  DateField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  ICON_SIZE,
  InputLabel,
  LabelWithIcon,
  Link as MuiLink,
  MarkdownField,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@pulse/ui';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { AttachmentsPanel } from '../../../components/AttachmentsPanel';
import { CopyLinkButton } from '../../../components/CopyLinkButton';
import { EntityPicker } from '../../../components/EntityPicker';
import { LogEntriesPanel } from '../../../components/LogEntriesPanel';
import { MultiSelectPicker } from '../../../components/MultiSelectPicker';
import { StagedAttachmentsPanel, useStagedAttachments } from '../../../components/StagedAttachmentsPanel';
import { useUserLookup } from '../../access-management/hooks/useUsers';
import { riskAttachmentDownloadUrl, uploadRiskAttachment } from '../api/risk.api';
import { useDependencies } from '../hooks/useDependencies';
import { useIssues } from '../hooks/useIssues';
import {
  useAddRiskLogEntry,
  useCreateRisk,
  useRemoveRiskAttachment,
  useRiskAttachments,
  useRiskLog,
  useTransitionRisk,
  useUpdateRisk,
  useUploadRiskAttachment,
} from '../hooks/useRisks';
import { useWorkflowDefinition } from '../hooks/useWorkflow';
import { riskLink } from '../utils/entityLinks';

const IMPACTS: RiskImpact[] = ['Severe', 'Major', 'Moderate', 'Minor', 'Negligible'];
const PROBABILITIES: RiskProbability[] = ['Almost Certain', 'Likely', 'Possible', 'Unlikely', 'Rare'];
// Matches CreateRiskDto's @MaxLength decorators (apps/api/src/modules/presales/dto/create-risk.dto.ts).
const TITLE_MAX = 255;
const TEXT_MAX = 1000;

function overLimitHelperText(value: string, max: number, overLimit: boolean): string {
  return overLimit ? `${value.length}/${max} — ${value.length - max} characters over the limit` : `${value.length}/${max}`;
}

// Stable (module-level) empty-array fallbacks — an inline `= []` default is a fresh reference
// on every render while the query is loading, which would make the resync useEffect below
// (which depends on `issues`/`dependencies`) fire on every render and loop.
const EMPTY_ISSUES: IIssue[] = [];
const EMPTY_DEPENDENCIES: IDependency[] = [];

interface RiskFormDialogProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
  risk: IRisk | null;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
  /** Fired only when an existing risk's status is changed *to* "Escalated" (§12c) — the parent
   *  wires this to the Risk->Escalation confirm prompt once Escalations (Phase 4) exist. */
  onEscalated?: (risk: IRisk) => void;
}

export function RiskFormDialog({ projectId, open, onClose, risk, onSaved, onError, onEscalated }: RiskFormDialogProps) {
  const isEdit = Boolean(risk);
  const { data: users = [] } = useUserLookup();
  const { data: issues = EMPTY_ISSUES } = useIssues(projectId);
  const { data: dependencies = EMPTY_DEPENDENCIES } = useDependencies(projectId);
  // Status options come from the DB workflow_steps for entityType 'risk' — never hardcoded.
  const { data: workflowDefinition } = useWorkflowDefinition('risk');
  const statusOptions = workflowDefinition?.steps.map((s) => s.name) ?? [];

  const [title, setTitle] = useState(risk?.title ?? '');
  const [impact, setImpact] = useState<RiskImpact | ''>(risk?.impact ?? '');
  const [probability, setProbability] = useState<RiskProbability | ''>(risk?.probability ?? '');
  const [ownerId, setOwnerId] = useState(risk?.ownerId ?? '');
  const [description, setDescription] = useState(risk?.description ?? '');
  const [managementStrategy, setManagementStrategy] = useState(risk?.managementStrategy ?? '');
  const [targetResolutionDate, setTargetResolutionDate] = useState(risk?.targetResolutionDate ?? '');
  // Staged locally like every other field — the Select shows this, not risk.status directly, so
  // picking a new status doesn't touch the backend until Save (see handleSave below).
  const [pendingStatus, setPendingStatus] = useState(risk?.status ?? '');
  const [linkedIssues, setLinkedIssues] = useState<IIssue[]>(issues.filter((i) => risk?.linkedIssueIds.includes(i.id)));
  const [linkedDependencies, setLinkedDependencies] = useState<IDependency[]>(
    dependencies.filter((d) => risk?.linkedDependencyIds.includes(d.id)),
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [targetResolutionDateFieldValid, setTargetResolutionDateFieldValid] = useState(true);
  const {
    staged,
    add: addStaged,
    remove: removeStaged,
    reset: resetStaged,
    uploadAll,
    isUploading: isUploadingStaged,
  } = useStagedAttachments();

  // The dialog is mounted once and toggled via `open` rather than remounted per edit, so the
  // `useState(risk?.x ?? '')` initializers above only ever run for whichever risk was current
  // on first mount — this re-syncs the form to the risk actually being edited (or clears it
  // back to blank for a new one) whenever the target, open-state, or the linkable-issue/
  // dependency lists change (same pattern as SprintFormDialog.tsx).
  useEffect(() => {
    setTitle(risk?.title ?? '');
    setImpact(risk?.impact ?? '');
    setProbability(risk?.probability ?? '');
    setOwnerId(risk?.ownerId ?? '');
    setDescription(risk?.description ?? '');
    setManagementStrategy(risk?.managementStrategy ?? '');
    setTargetResolutionDate(risk?.targetResolutionDate ?? '');
    setPendingStatus(risk?.status ?? '');
    setLinkedIssues(issues.filter((i) => risk?.linkedIssueIds.includes(i.id)));
    setLinkedDependencies(dependencies.filter((d) => risk?.linkedDependencyIds.includes(d.id)));
    setFormError(null);
    setTargetResolutionDateFieldValid(true);
    resetStaged();
  }, [risk, open, issues, dependencies, resetStaged]);

  const createMutation = useCreateRisk(projectId);
  const updateMutation = useUpdateRisk(projectId);
  const transitionMutation = useTransitionRisk(projectId);
  const { data: logEntries = [], isLoading: logLoading } = useRiskLog(projectId, risk?.id ?? '');
  const addLogMutation = useAddRiskLogEntry(projectId, risk?.id ?? '');
  const { data: attachments = [], isLoading: attachmentsLoading } = useRiskAttachments(projectId, risk?.id ?? '');
  const uploadMutation = useUploadRiskAttachment(projectId, risk?.id ?? '');
  const removeAttachmentMutation = useRemoveRiskAttachment(projectId, risk?.id ?? '');

  const titleOverLimit = title.length > TITLE_MAX;
  const descriptionOverLimit = description.length > TEXT_MAX;
  const managementStrategyOverLimit = managementStrategy.length > TEXT_MAX;
  const isFormValid =
    title.trim().length > 0 && impact !== '' && probability !== '' && ownerId !== '' &&
    !titleOverLimit && !descriptionOverLimit && !managementStrategyOverLimit &&
    targetResolutionDateFieldValid;

  function handleSave() {
    const payload = {
      title: title.trim(),
      impact: impact as RiskImpact,
      probability: probability as RiskProbability,
      ownerId,
      description: description.trim() || null,
      managementStrategy: managementStrategy.trim() || null,
      targetResolutionDate: targetResolutionDate || null,
      linkedIssueIds: linkedIssues.map((i) => i.id),
      linkedDependencyIds: linkedDependencies.map((d) => d.id),
    };
    if (isEdit && risk) {
      const statusChanged = pendingStatus !== risk.status;
      if (!statusChanged) {
        updateMutation.mutate(
          { riskId: risk.id, data: payload },
          {
            onSuccess: () => { setFormError(null); onSaved('Risk updated successfully.'); onClose(); },
            onError: (err: Error) => setFormError(err.message),
          },
        );
        return;
      }

      // Status changed (possibly along with other fields) — the workflow transition and the
      // field update are separate, non-transactional backend endpoints (see risk.service.ts),
      // so they're combined into one Save action here rather than fired independently on every
      // field change. Transition fires first: it's the one independently validated against the
      // risk's WorkflowDefinition, so a rejection there leaves nothing written at all instead of
      // a partially-saved record.
      const original = {
        title: risk.title,
        impact: risk.impact,
        probability: risk.probability,
        ownerId: risk.ownerId,
        description: risk.description || undefined,
        managementStrategy: risk.managementStrategy || undefined,
        targetResolutionDate: risk.targetResolutionDate || undefined,
        linkedIssueIds: [...risk.linkedIssueIds].sort(),
        linkedDependencyIds: [...risk.linkedDependencyIds].sort(),
      };
      const current = {
        ...payload,
        linkedIssueIds: [...payload.linkedIssueIds].sort(),
        linkedDependencyIds: [...payload.linkedDependencyIds].sort(),
      };
      const fieldsChanged = JSON.stringify(current) !== JSON.stringify(original);

      void (async () => {
        try {
          const updated = await transitionMutation.mutateAsync({ riskId: risk.id, toStep: pendingStatus });
          if (pendingStatus === 'Escalated') onEscalated?.(updated);

          if (fieldsChanged) {
            try {
              await updateMutation.mutateAsync({ riskId: risk.id, data: payload });
            } catch (err) {
              // Transition already committed (and its own cache invalidation already made the
              // table consistent) — report exactly what happened rather than a vague combined
              // message. Re-opening Edit is the correct way to retry just the field changes.
              setFormError(
                `Status changed to "${pendingStatus}" successfully, but the other changes could not be saved: ${err instanceof Error ? err.message : 'unknown error'}.`,
              );
              onClose();
              return;
            }
          }

          setFormError(null);
          onSaved(fieldsChanged ? 'Risk updated successfully.' : `Risk moved to "${pendingStatus}".`);
          onClose();
        } catch (err) {
          setFormError(err instanceof Error ? err.message : 'Failed to change status.');
        }
      })();
      return;
    }
    const filesToUpload = staged;
    createMutation.mutate(payload, {
      onSuccess: (created) => {
        void (async () => {
          const uploadOk =
            filesToUpload.length === 0 ||
            (await uploadAll(filesToUpload, (file) => uploadRiskAttachment(projectId, created.id, file)));
          resetStaged();
          setFormError(null);
          onSaved(
            uploadOk
              ? 'Risk created successfully.'
              : 'Risk created successfully, but some attachments failed to upload.',
          );
          onClose();
        })();
      },
      onError: (err: Error) => setFormError(err.message),
    });
  }

  const isSaving = createMutation.isPending || updateMutation.isPending || transitionMutation.isPending || isUploadingStaged;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ wordBreak: 'break-word' }}>
        {isEdit && risk ? (
          <MuiLink
            component={RouterLink}
            to={riskLink(projectId, risk.riskKey)}
            onClick={onClose}
            sx={{ wordBreak: 'break-word' }}
          >
            {risk.riskKey} — {risk.title}
          </MuiLink>
        ) : (
          <span>Raise a Risk</span>
        )}
        {isEdit && risk && (
          <span style={{ marginLeft: 6, verticalAlign: 'middle' }}>
            <CopyLinkButton path={riskLink(projectId, risk.riskKey)} />
          </span>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}

          {isEdit && risk ? (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel><LabelWithIcon label="Status" icon={<TrendingUpIcon fontSize={ICON_SIZE.sm} />} inline /></InputLabel>
                <Select
                  value={pendingStatus}
                  label={<LabelWithIcon label="Status" icon={<TrendingUpIcon fontSize={ICON_SIZE.sm} />} inline />}
                  onChange={(e) => setPendingStatus(e.target.value)}
                  disabled={isSaving}
                >
                  {statusOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              {!risk.isActive && <Chip size="small" label="Inactive" color="default" />}
            </Stack>
          ) : (
            <TextField
              label={<LabelWithIcon label="Status" icon={<TrendingUpIcon fontSize={ICON_SIZE.sm} />} inline />}
              value={workflowDefinition?.steps.find((s) => s.isInitial)?.name ?? statusOptions[0] ?? ''}
              disabled
              size="small"
              sx={{ maxWidth: 200 }}
              helperText="New risks start in this status"
            />
          )}

          <TextField
            label={<LabelWithIcon label="Title" icon={<ShortTextIcon fontSize={ICON_SIZE.sm} />} inline />}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => setTitle((t) => t.trim())}
            required
            fullWidth
            error={titleOverLimit}
            helperText={overLimitHelperText(title, TITLE_MAX, titleOverLimit)}
          />

          {/* Two fields per row instead of all four in one — cramming Impact/Probability/Owner/
              Target Resolution Date into a single row left too little width per field at tablet
              sizes (sm-md), truncating the Probability Select and the Date field. */}
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth required>
                <InputLabel><LabelWithIcon label="Impact" icon={<PriorityHighIcon fontSize={ICON_SIZE.sm} />} inline /></InputLabel>
                <Select
                  value={impact}
                  label={<LabelWithIcon label="Impact" icon={<PriorityHighIcon fontSize={ICON_SIZE.sm} />} inline />}
                  onChange={(e) => setImpact(e.target.value as RiskImpact)}
                >
                  {IMPACTS.map((i) => <MenuItem key={i} value={i}>{i}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel><LabelWithIcon label="Probability" icon={<PercentIcon fontSize={ICON_SIZE.sm} />} inline /></InputLabel>
                <Select
                  value={probability}
                  label={<LabelWithIcon label="Probability" icon={<PercentIcon fontSize={ICON_SIZE.sm} />} inline />}
                  onChange={(e) => setProbability(e.target.value as RiskProbability)}
                >
                  {PROBABILITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <EntityPicker
                label="Owner"
                labelIcon={<PersonIcon sx={{ fontSize: ICON_SIZE.xs, verticalAlign: 'middle', mr: 0.5, position: 'relative' }} />}
                required
                options={users}
                value={users.find((u) => u.id === ownerId) ?? null}
                onChange={(u) => setOwnerId(u?.id ?? '')}
              />
              <DateField
                label={<LabelWithIcon label="Target Resolution Date" icon={<EventIcon fontSize={ICON_SIZE.sm} />} inline />}
                value={targetResolutionDate}
                onChange={setTargetResolutionDate}
                onValidityChange={setTargetResolutionDateFieldValid}
                invalidMessage="Please enter a valid Target Resolution Date."
                fullWidth
              />
            </Stack>
          </Stack>

          <MarkdownField
            label={<LabelWithIcon label="Description" icon={<NotesIcon fontSize={ICON_SIZE.sm} />} inline />}
            value={description}
            onChange={setDescription}
            error={descriptionOverLimit}
            helperText={overLimitHelperText(description, TEXT_MAX, descriptionOverLimit)}
          />
          <MarkdownField
            label={<LabelWithIcon label="Risk Management Strategy" icon={<ShieldIcon fontSize={ICON_SIZE.sm} />} inline />}
            value={managementStrategy}
            onChange={setManagementStrategy}
            error={managementStrategyOverLimit}
            helperText={overLimitHelperText(managementStrategy, TEXT_MAX, managementStrategyOverLimit)}
          />

          <MultiSelectPicker<IIssue>
            label="Link Issues"
            labelIcon={<LinkIcon fontSize={ICON_SIZE.sm} />}
            options={issues}
            value={linkedIssues}
            onChange={setLinkedIssues}
            getOptionKey={(i) => i.id}
            getOptionLabel={(i) => `${i.issueKey} — ${i.name}`}
          />
          <MultiSelectPicker<IDependency>
            label="Link Dependencies"
            labelIcon={<LinkIcon fontSize={ICON_SIZE.sm} />}
            options={dependencies}
            value={linkedDependencies}
            onChange={setLinkedDependencies}
            getOptionKey={(d) => d.id}
            getOptionLabel={(d) => `${d.dependencyKey} — ${d.title}`}
          />

          <Divider />
          <Typography variant="subtitle2">
            <LabelWithIcon label="Attachments" icon={<AttachFileIcon fontSize={ICON_SIZE.sm} sx={{ color: 'text.secondary' }} />} />
          </Typography>
          {isEdit && risk ? (
            <AttachmentsPanel
              attachments={attachments}
              isLoading={attachmentsLoading}
              onUpload={(file) => uploadMutation.mutate(file, { onError: (err: Error) => onError(err.message) })}
              isUploading={uploadMutation.isPending}
              onRemove={(id) =>
                removeAttachmentMutation.mutate(id, {
                  onSuccess: () => onSaved('Attachment deleted successfully.'),
                  onError: (err: Error) => onError(err.message),
                })
              }
              isRemoving={removeAttachmentMutation.isPending}
              downloadUrl={(id) => riskAttachmentDownloadUrl(projectId, risk.id, id)}
            />
          ) : (
            <StagedAttachmentsPanel staged={staged} onAdd={addStaged} onRemove={removeStaged} isUploading={isUploadingStaged} />
          )}

          {isEdit && risk && (
            <>
              <Divider />
              <Typography variant="subtitle2">
                <LabelWithIcon label="Log" icon={<HistoryIcon fontSize={ICON_SIZE.sm} sx={{ color: 'text.secondary' }} />} />
              </Typography>
              <LogEntriesPanel
                entries={logEntries}
                isLoading={logLoading}
                onAdd={(note) => addLogMutation.mutate(note, { onError: (err: Error) => onError(err.message) })}
                isAdding={addLogMutation.isPending}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!isFormValid || isSaving}>
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
